import { ArrowLeftFromLine, Plus, Tags } from 'lucide-react';
import { useState, useMemo } from 'react';

import { TagType } from '../../redux/Slices/tags';
import { useFetchAssociatedStudentsQuery } from '../../services/scheduleTestApi';
import { useLazyFetchTagByIdQuery } from '../../services/tagsApi';
import { useFetchTagsQuery } from '../../services/tagsApi';

import TagCreationDialog from './TagCreationDialog';

import TagsTable from '.';

const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message }: any) => {
  if (!isOpen) return null;
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
      <div className='w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl'>
        <h3 className='mb-3 text-xl font-bold text-gray-900'>{title}</h3>
        <p className='mb-6 text-gray-600'>{message}</p>
        <div className='flex justify-end gap-3'>
          <button
            onClick={onClose}
            className='rounded-lg border border-gray-300 px-5 py-2.5 font-semibold text-gray-700 hover:bg-gray-50'
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className='rounded-lg bg-red-600 px-5 py-2.5 font-semibold text-white hover:bg-red-700'
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const TagManagementScreen = ({ onBack }: any) => {
  const { data: tagsResponse } = useFetchTagsQuery({});
  const tags = tagsResponse?.data || [];

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<TagType | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { data: associatedStudents } = useFetchAssociatedStudentsQuery({});
  const studentMap = useMemo(() => {
    if (!associatedStudents?.data) return {};

    return associatedStudents.data.reduce((acc: any, s: any) => {
      acc[s.studentId] = s.name;
      return acc;
    }, {});
  }, [associatedStudents]);

  const [fetchTagById] = useLazyFetchTagByIdQuery();

  const handleSave = (data: any, tagId?: string) => {
    setIsFormOpen(false);
    setEditingTag(null);
  };

  const handleEdit = async (tag: TagType) => {
    try {
      const res = await fetchTagById(tag?.id).unwrap();
      setEditingTag(tag);
      setIsFormOpen(true);
    } catch (err) {
      console.error('Failed to fetch tag details:', err);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeletingId(id);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleteOpen(false);
    setDeletingId(null);
  };
  return (
    <div className='min-h-screen bg-gray-50 p-8'>
      <div className='mx-auto max-w-6xl'>
        <button
          onClick={onBack}
          className='mb-5 flex items-center rounded-lg bg-white px-4 py-2 font-semibold text-gray-700 shadow-sm hover:bg-gray-50'
        >
          <ArrowLeftFromLine size={20} />{' '}
          <span className='ml-1 mt-[2px]'>Back</span>
        </button>
      </div>
      <div className='mx-auto max-w-6xl'>
        <div className='mb-3 flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-3'>
              <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100'>
                <Tags className='h-6 w-6 text-blue-600' />
              </div>
              <div>
                <h1 className='text-2xl font-bold text-gray-900'>
                  Batch Management
                </h1>
                <p className='text-sm text-gray-600'>
                  Create and manage student Batches
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingTag(null);
              setIsFormOpen(true);
            }}
            className='flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-700'
          >
            <Plus className='h-5 w-5' />
            Create Batch
          </button>
        </div>

        <div className='mb-4 rounded-2xl'>
          <div className='flex items-center gap-4'>
            {/* <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100'>
              <Tags className='h-5 w-5 text-blue-600' />
            </div> */}
            {/* <div className='flex items-center gap-2'>
              <div className='flex items-center gap-1'>
                <Tag className='h-4 w-4 text-blue-600' />
                <span className='text-xl font-bold text-gray-900'>
                  {tags.length}
                </span>
              </div>

              <span className='text-sm font-semibold text-blue-600'>
                Total Tags Created
              </span>
            </div> */}
          </div>
        </div>

        <TagsTable
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          associatedStudents={associatedStudents?.data || []}
        />
        <TagCreationDialog
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingTag(null);
          }}
          onSave={handleSave}
          existingTags={tags}
          editingTag={editingTag}
          associatedStudents={associatedStudents?.data || []}
        />

        <ConfirmDialog
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setDeletingId(null);
          }}
          onConfirm={handleConfirmDelete}
          title='Delete Batch'
          message='Are you sure you want to delete this batch? This action cannot be undone.'
        />
      </div>
    </div>
  );
};

export default TagManagementScreen;
